import { withIDBFallback, IDB_STORES, IDB_TTL } from "./IDBCacheService";
import { getApiBase, withPartNumberSkuQuery } from "../helpers/helpers";
import { awaitModelsSeed, getSeedFromMemory } from "./SeedPrefetchService";

/**
 * Fetch the vehicles/availability endpoint with a 4-tier fallback:
 *   1. Fresh IDB cache (within TTL)  → return immediately + background-refresh
 *   2. Live network                  → store in IDB, return
 *   3. Stale IDB (any age)           → return stale data rather than failing
 *   4. In-memory seed                → final fallback for race between seed
 *                                       loading and query execution (offline mode)
 *
 * The function returns the full parsed JSON response object.
 */
export async function fetchVehicleAvailability(
  year: string | number,
  make: string,
  model: string,
): Promise<unknown> {
  // v3 suffix invalidates any pre-display_mode caches (added 2026-06-19).
  // Only applies to the real IDB cache key — the bundled seed file (written
  // by scripts/generate-idb-seed.js) never had the pre-display_mode shape,
  // so it's still stored and looked up under the unversioned key.
  const idbKey = `vehicle_availability_v3_${year}_${make}_${model}`;
  const seedKey = `vehicle_availability_${year}_${make}_${model}`;

  // Ensure the models shard for this year is loaded into both IDB and memory
  // before we check IDB. This prevents the race where the user navigates
  // year → make → model faster than the seed finishes writing to IDB.
  await awaitModelsSeed(year);

  let raw: unknown;
  try {
    raw = await withIDBFallback(
      IDB_STORES.VEHICLE_AVAILABILITY,
      idbKey,
      IDB_TTL.VEHICLE_AVAILABILITY,
      async () => {
        const base = getApiBase();
        const url = withPartNumberSkuQuery(
          `${base}/api/v1/vehicles/availability` +
          `?year=${encodeURIComponent(String(year))}` +
          `&make=${encodeURIComponent(String(make))}` +
          `&model=${encodeURIComponent(String(model))}`,
        );

        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch vehicle availability");
        return res.json();
      },
    );
  } catch (err) {
    // 4. Network and IDB both failed — try in-memory seed as last resort
    const seeded = getSeedFromMemory(IDB_STORES.VEHICLE_AVAILABILITY, seedKey);
    if (seeded !== undefined) {
      raw = seeded;
    } else {
      throw err;
    }
  }

  // Normalize shape: the seed stores the inner data directly as
  // { vehicle, availability, configurator } with no outer wrapper,
  // while the live API returns { message, data: { vehicle, availability, configurator } }.
  // Always return the live-API shape so consumers can rely on `response.data.*`.
  if (raw && typeof raw === "object" && !("data" in (raw as object))) {
    return { data: raw };
  }
  return raw;
}

/**
 * Fetch availability for a specific YMM + trim + cab combination.
 * Used to check whether this exact variant has configurator questions.
 * Not cached in IDB — it is a dynamic query driven by live user selection —
 * but falls back to the bundled trim/cab seed (generator's --trim-cab pass,
 * see scripts/generate-idb-seed.js) on network failure, for vehicles whose
 * Bundle/rear-seat SKU depends on trim/cab (show_trim_cab: true).
 */
export async function fetchVehicleAvailabilityWithTrimCab(
  year: string | number,
  make: string,
  model: string,
  trim: string,
  cab: string,
): Promise<unknown> {
  const base = getApiBase();
  const url = withPartNumberSkuQuery(
    `${base}/api/v1/vehicles/availability` +
    `?year=${encodeURIComponent(String(year))}` +
    `&make=${encodeURIComponent(String(make))}` +
    `&model=${encodeURIComponent(String(model))}` +
    `&trim=${encodeURIComponent(String(trim))}` +
    `&cab=${encodeURIComponent(String(cab))}`,
  );

  let raw: unknown;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Failed to fetch trim/cab availability");
    raw = await res.json();
  } catch (err) {
    const seedKey = `vehicle_availability_${year}_${make}_${model}_${trim}_${cab}`;
    const seeded = getSeedFromMemory(IDB_STORES.VEHICLE_AVAILABILITY, seedKey);
    if (seeded !== undefined) {
      raw = seeded;
    } else {
      throw err;
    }
  }

  if (raw && typeof raw === "object" && !("data" in (raw as object))) {
    return { data: raw };
  }
  return raw;
}
